import React, { useState, useEffect } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { Trash2, Edit, Plus, AlertTriangle, X, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

function ExamManagement() {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('exammanagement');
  const isRTL = i18n.language === 'ar';
  
  // State management
  const [exams, setExams] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalType, setModalType] = useState('create'); // 'create' or 'edit'
  const [selectedExam, setSelectedExam] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  
  // Filter states
  const [selectedAcademicYear, setSelectedAcademicYear] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [availableFilterTerms, setAvailableFilterTerms] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    exam_name: '',
    academic_year_id: '',
    term_id: '',
    class_ids: []
  });
  
  // Form errors
  const [formErrors, setFormErrors] = useState({});
  
  // Fetch all necessary data on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          fetchExams(),
          fetchAcademicYears(),
          fetchClasses()
        ]);
      } catch (err) {
        setError(t('errors.failedToLoadData'));
      } finally {
        setLoading(false);
      }
    };
    
    fetchInitialData();
  }, [t]);
  
  // Update available terms for filtering when academic year changes
  useEffect(() => {
    if (selectedAcademicYear) {
      const year = academicYears.find(y => y.academic_year_id === parseInt(selectedAcademicYear));
      setAvailableFilterTerms(year ? year.terms : []);
    } else {
      setAvailableFilterTerms([]);
    }
    // Reset term selection when academic year changes
    setSelectedTerm('');
  }, [selectedAcademicYear, academicYears]);

  //Set the current academic year as the default once the academic years have been fetched
  useEffect(()=>
  {
    let defaultAcademicYear = academicYears?.find(year => year.is_current_year === true)
    if(defaultAcademicYear)
    {
      setSelectedAcademicYear(defaultAcademicYear.academic_year_id)
    }
    else
    {
      setSelectedAcademicYear("")
    }
  },[academicYears])
  
  // Fetch exams from API
  const fetchExams = async () => {
    try {
      const response = await api.get(`/api/v1/exams/school/${user.school_id}/all-exams`);
      setExams(response.data);
      return response.data;
    } catch (err) {
      handleApiError(err);
      return [];
    }
  };
  
  // Fetch academic years with terms
  const fetchAcademicYears = async () => {
    try {
      const response = await api.get('/api/v1/academic-years/list-with-terms');
      setAcademicYears(response.data);
      return response.data;
    } catch (err) {
      handleApiError(err);
      return [];
    }
  };
  
  // Fetch classes
  const fetchClasses = async () => {
    try {
      const response = await api.get(`/api/v1/admin/schools/${user.school_reg}/classes`);
      setClasses(response.data);
      return response.data;
    } catch (err) {
      handleApiError(err);
      return [];
    }
  };
  
  // Create new exam
  const createExam = async () => {
    try {
      const response = await api.post(`/api/v1/exams/school/${user.school_id}/create`, formData);
      const updatedExams = await fetchExams();
      resetForm();
      setShowModal(false);
      showNotification(t('notifications.examCreated'), 'success');
      return response.data;
    } catch (err) {
      handleApiError(err);
      return null;
    }
  };
  
  // Update exam
  const updateExam = async () => {
    try {
      const response = await api.put(`/api/v1/exams/${selectedExam.id}/school/${user.school_id}/update`, formData);
      const updatedExams = await fetchExams();
      resetForm();
      setShowModal(false);
      showNotification(t('notifications.examUpdated'), 'success');
      return response.data;
    } catch (err) {
      handleApiError(err);
      return null;
    }
  };
  
  // Delete exam
  const deleteExam = async () => {
    try {
      await api.delete(`/api/v1/exams/${selectedExam.id}/school/${user.school_id}/delete`);
      // Store the exam ID before clearing the selected exam
      const deletedExamId = selectedExam.id;
      setShowDeleteModal(false);
      setSelectedExam(null);
      
      // Update the exams state directly to reflect the deletion
      setExams(prevExams => prevExams.filter(exam => exam.id !== deletedExamId));
      
      // Also fetch fresh data from the server
      fetchExams();
      showNotification(t('notifications.examDeleted'), 'success');
    } catch (err) {
      handleApiError(err);
    }
  };
  
  // Navigate to exam details page
  const navigateToExamDetails = (examId, e) => {
    // Prevent navigation if user clicked on action buttons
    if (e.target.closest('button')) return;
    navigate(`/exam-config-manager/${examId}`);
  };
  
  // Handle API errors
  const handleApiError = (err) => {
    if (err.response && err.response.data && err.response.data.detail) {
      // Handle structured error response
      const errorDetails = err.response.data.detail;
      const errorMessages = {};
      
      if (Array.isArray(errorDetails)) {
        errorDetails.forEach(detail => {
          // Extract field name from location path
          const fieldName = detail.loc[0] === 'body' ? detail.loc[1] : detail.loc[0];
          errorMessages[fieldName] = detail.msg;
        });
        setFormErrors(errorMessages);
        
        // Set the first error message as the main error
        if (errorDetails.length > 0) {
          setError(errorDetails[0].msg);
        }
      } else {
        // Handle string error message
        setError(errorDetails);
      }
    } else {
      // Handle generic error
      setError(err.message || t('errors.unexpectedError'));
    }
  };
  
  // Show notification
  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 5000);
  };
  
  // Reset form
  const resetForm = () => {
    setFormData({
      exam_name: '',
      academic_year_id: '',
      term_id: '',
      class_ids: []
    });
    setFormErrors({});
  };
  
  // Open create modal
  const openCreateModal = () => {
    setModalType('create');
    resetForm();
    setShowModal(true);
  };
  
  // Open edit modal
  const openEditModal = (exam) => {
    setModalType('edit');
    setSelectedExam(exam);
    setFormData({
      exam_name: exam.exam_name,
      academic_year_id: exam.academic_year_id,
      term_id: exam.term_id,
      class_ids: exam.class_ids || []
    });
    setShowModal(true);
  };
  
  // Open delete confirmation modal
  const openDeleteModal = (exam) => {
    setSelectedExam(exam);
    setShowDeleteModal(true);
  };
  
  // Clear error
  const clearError = () => {
    setError(null);
  };
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for academic_year_id to reset term_id
    if (name === 'academic_year_id') {
      setFormData({ 
        ...formData, 
        [name]: value,
        term_id: '' // Reset term when academic year changes
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
    
    // Clear error for this field if it exists
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: null });
    }
  };
  
  // Handle class selection
  const handleClassSelection = (classId) => {
    const updatedClassIds = formData.class_ids.includes(classId)
      ? formData.class_ids.filter(id => id !== classId)
      : [...formData.class_ids, classId];
    
    setFormData({ ...formData, class_ids: updatedClassIds });
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = {};
    if (!formData.exam_name.trim()) errors.exam_name = t('validation.examNameRequired');
    if (!formData.academic_year_id) errors.academic_year_id = t('validation.academicYearRequired');
    if (!formData.term_id) errors.term_id = t('validation.termRequired');
    if (formData.class_ids.length === 0) errors.class_ids = t('validation.classRequired');
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    // Submit form based on modal type
    if (modalType === 'create') {
      createExam();
    } else {
      updateExam();
    }
  };
  
  // Get filtered exams based on selection
  const getFilteredExams = () => {
    return exams.filter(exam => {
      const academicYearMatch = !selectedAcademicYear || exam.academic_year_id === parseInt(selectedAcademicYear);
      const termMatch = !selectedTerm || exam.term_id === parseInt(selectedTerm);
      const classMatch = !selectedClass || (exam.class_ids && exam.class_ids.includes(parseInt(selectedClass)));
      return academicYearMatch && termMatch && classMatch;
    });
  };
  
  // Get academic year and term names for display
  const getAcademicYearName = (academicYearId) => {
    const year = academicYears.find(y => y.academic_year_id === academicYearId);
    return year ? year.year_name : t('common.unknown');
  };
  
  const getTermName = (academicYearId, termId) => {
    const year = academicYears.find(y => y.academic_year_id === academicYearId);
    if (!year) return t('common.unknown');
    
    const term = year.terms.find(t => t.term_id === termId);
    return term ? term.term_name : t('common.unknown');
  };
  
  const getClassNames = (classIds) => {
    if (!classIds || !Array.isArray(classIds) || classIds.length === 0) return t('common.none');
    
    return classIds.map(classId => {
      const classObj = classes.find(c => c.id === classId);
      return classObj ? classObj.name : `${t('common.class')} ${classId}`;
    }).join(', ');
  };
  
  // Get available terms for selected academic year in the form
  const getAvailableTerms = () => {
    if (!formData.academic_year_id) return [];
    const selectedYear = academicYears.find(y => y.academic_year_id === parseInt(formData.academic_year_id));
    return selectedYear ? selectedYear.terms : [];
  };
  
  const filteredExams = getFilteredExams();

  return (
    <div className={`w-full ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Success Notification */}
      {notification.show && notification.type === 'success' && (
        <div className={`fixed top-4 ${isRTL ? 'left-4' : 'right-4'} z-50 p-4 rounded-lg shadow-lg flex items-center justify-between max-w-md bg-green-100 text-green-800`}>
          <div className="flex items-center">
            <Check className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
            <p>{notification.message}</p>
          </div>
          <button 
            onClick={() => setNotification({ show: false, message: '', type: '' })}
            className={`${isRTL ? 'mr-4' : 'ml-4'} text-gray-500 hover:text-gray-700`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">{t('header.title')}</h1>
        <p className="text-gray-600">{t('header.subtitle')}</p>
      </div>
      
      {/* Error Display */}
      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-800 rounded-lg flex items-center justify-between">
          <div className="flex items-center">
            <AlertTriangle className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
            <p>{error}</p>
          </div>
          <button
            onClick={clearError}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      
      {/* Actions Bar */}
      <div className="flex flex-col md:flex-row mb-6 gap-4">
        <button
          onClick={openCreateModal}
          className="bg-[#0E5252] hover:bg-[#0a3e3e] text-white px-4 py-2 rounded-lg flex items-center justify-center md:justify-start"
        >
          <Plus className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} />
          {t('buttons.createNewExam')}
        </button>
        
        <div className="flex-grow flex flex-col md:flex-row gap-3">
          {/* Academic Year Filter */}
          <select
            value={selectedAcademicYear}
            onChange={(e) => setSelectedAcademicYear(e.target.value)}
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0E5252]"
          >
            <option value="">{t('filters.allAcademicYears')}</option>
            {academicYears.map(year => (
              <option key={year.academic_year_id} value={year.academic_year_id}>
                {year.year_name} {year.is_current_year ? `(${t('common.current')})` : ''}
              </option>
            ))}
          </select>
          
          {/* Term Filter */}
          <select
            value={selectedTerm}
            onChange={(e) => setSelectedTerm(e.target.value)}
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0E5252]"
            disabled={!selectedAcademicYear}
          >
            <option value="">{t('filters.allTerms')}</option>
            {availableFilterTerms.map(term => (
              <option key={term.term_id} value={term.term_id}>
                {term.term_name}
              </option>
            ))}
          </select>
          
          {/* Class Filter */}
          <select
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value)}
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#0E5252]"
          >
            <option value="">{t('filters.allClasses')}</option>
            {classes.map(cls => (
              <option key={cls.id} value={cls.id}>
                {cls.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Content */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center p-10">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0E5252]"></div>
          </div>
        ) : filteredExams.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-10 text-gray-500">
            <p className="text-lg font-semibold mb-2">{t('messages.noExamsFound')}</p>
            <p className="text-gray-400 text-center max-w-md">
              {exams.length === 0 
                ? t('messages.noExamsYet')
                : t('messages.noExamsMatchFilter')}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                    {t('table.examName')}
                  </th>
                  <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                    {t('table.academicYear')}
                  </th>
                  <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                    {t('table.term')}
                  </th>
                  <th className={`px-6 py-3 ${isRTL ? 'text-left' : 'text-right'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                    {t('table.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredExams.map((exam) => (
                  <tr 
                    key={exam.id} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={(e) => navigateToExamDetails(exam.id, e)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap max-w-xs overflow-x-auto">
                      <div className="font-medium text-gray-900">{exam.exam_name}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap max-w-xs overflow-x-auto text-gray-700">
                      {getAcademicYearName(exam.academic_year_id)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap max-w-xs overflow-x-auto text-gray-700">
                      {getTermName(exam.academic_year_id, exam.term_id)}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap ${isRTL ? 'text-left' : 'text-right'} text-sm font-medium`}>
                      <div className={`flex ${isRTL ? 'justify-end' : 'justify-end'} space-x-2 ${isRTL ? 'space-x-reverse' : ''}`}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(exam);
                          }}
                          className="text-[#0E5252] hover:text-[#0a3e3e] p-1 rounded-full hover:bg-gray-100"
                          title={t('buttons.editExam')}
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openDeleteModal(exam);
                          }}
                          className="text-red-500 hover:text-red-700 p-1 rounded-full hover:bg-red-50"
                          title={t('buttons.deleteExam')}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Create/Edit Exam Modal */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">
                {modalType === 'create' ? t('modal.createTitle') : t('modal.editTitle')}
              </h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Exam Name */}
              <div>
                <label htmlFor="exam_name" className="block mb-1 font-medium text-gray-700">
                  {t('form.examName')} *
                </label>
                <input
                  type="text"
                  id="exam_name"
                  name="exam_name"
                  value={formData.exam_name}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0E5252] ${
                    formErrors.exam_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder={t('form.examNamePlaceholder')}
                />
                {formErrors.exam_name && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.exam_name}</p>
                )}
              </div>
              
              {/* Academic Year */}
              <div>
                <label htmlFor="academic_year_id" className="block mb-1 font-medium text-gray-700">
                  {t('form.academicYear')} *
                </label>
                <select
                  id="academic_year_id"
                  name="academic_year_id"
                  value={formData.academic_year_id}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0E5252] ${
                    formErrors.academic_year_id ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">{t('form.selectAcademicYear')}</option>
                  {academicYears.map(year => (
                    <option key={year.academic_year_id} value={year.academic_year_id}>
                      {year.year_name} {year.is_current_year ? `(${t('common.current')})` : ''}
                    </option>
                  ))}
                </select>
                {formErrors.academic_year_id && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.academic_year_id}</p>
                )}
              </div>
              
              {/* Term */}
              <div>
                <label htmlFor="term_id" className="block mb-1 font-medium text-gray-700">
                  {t('form.term')} *
                </label>
                <select
                  id="term_id"
                  name="term_id"
                  value={formData.term_id}
                  onChange={handleInputChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0E5252] ${
                    formErrors.term_id ? 'border-red-500' : 'border-gray-300'
                  }`}
                  disabled={!formData.academic_year_id}
                >
                  <option value="">{t('form.selectTerm')}</option>
                  {getAvailableTerms().map(term => (
                    <option key={term.term_id} value={term.term_id}>
                      {term.term_name}
                    </option>
                  ))}
                </select>
                {!formData.academic_year_id && (
                  <p className="mt-1 text-sm text-gray-500">{t('form.selectAcademicYearFirst')}</p>
                )}
                {formErrors.term_id && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.term_id}</p>
                )}
              </div>
              
              {/* Classes */}
              <div>
                <label className="block mb-1 font-medium text-gray-700">
                  {t('form.classes')} *
                </label>
                <div className={`mt-2 p-3 border rounded-lg ${
                  formErrors.class_ids ? 'border-red-500' : 'border-gray-300'
                }`}>
                  {classes.length === 0 ? (
                    <p className="text-gray-500">{t('form.noClassesAvailable')}</p>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {classes.map(cls => (
                        <div key={cls.id} className="flex items-center">
                          <input
                            type="checkbox"
                            id={`class-${cls.id}`}
                            checked={formData.class_ids.includes(cls.id)}
                            onChange={() => handleClassSelection(cls.id)}
                            className="h-4 w-4 text-[#0E5252] focus:ring-[#0E5252] border-gray-300 rounded"
                          />
                          <label htmlFor={`class-${cls.id}`} className={`${isRTL ? 'mr-2' : 'ml-2'} text-sm text-gray-700`}>
                            {cls.name}
                          </label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {formErrors.class_ids && (
                  <p className="mt-1 text-sm text-red-500">{formErrors.class_ids}</p>
                )}
              </div>
              
              {/* Actions */}
              <div className={`flex ${isRTL ? 'justify-start' : 'justify-end'} space-x-3 ${isRTL ? 'space-x-reverse' : ''} pt-4 border-t border-gray-200`}>
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    setShowModal(false);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  {t('buttons.cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#0E5252] text-white rounded-lg hover:bg-[#0a3e3e]"
                >
                  {modalType === 'create' ? t('buttons.createExam') : t('buttons.updateExam')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedExam && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-800">{t('deleteModal.title')}</h2>
            </div>
            <div className="p-4">
              <p className="text-gray-700 mb-4">
                {t('deleteModal.confirmMessage', { examName: selectedExam.exam_name })}
              </p>
              <div className={`flex ${isRTL ? 'justify-start' : 'justify-end'} space-x-3 ${isRTL ? 'space-x-reverse' : ''} mt-4`}>
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
                >
                  {t('buttons.cancel')}
                </button>
                <button
                  type="button"
                  onClick={deleteExam}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md shadow-sm hover:bg-red-700"
                >
                  {t('buttons.delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ExamManagement;